import { useEffect, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { Modal } from '@/shared/components/Modal';
import type { PdvCardapioItem, PdvCardapioItemMarcacao } from '@/shared/types';

interface Props {
  item:      PdvCardapioItem;
  salvando:  boolean;
  onSalvar:  (dados: PdvCardapioItemMarcacao) => void;
  onFechar:  () => void;
  onUpload:  (file: File) => Promise<string>;
}

const inputClass = 'mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white';

export default function PdvItemModal({ item, salvando, onSalvar, onFechar, onUpload }: Props) {
  const [papel, setPapel] = useState(item.papel);
  const [nome, setNome] = useState(item.nome_exibicao ?? '');
  const [frase, setFrase] = useState(item.frase ?? '');
  const [imagem, setImagem] = useState<string | null>(item.imagem_url);
  const [video, setVideo] = useState<string | null>(item.video_url);
  const [enviando, setEnviando] = useState(false);
  const [erroUpload, setErroUpload] = useState<string | null>(null);
  useEffect(() => { setPapel(item.papel); setNome(item.nome_exibicao ?? ''); setFrase(item.frase ?? ''); setImagem(item.imagem_url); setVideo(item.video_url); }, [item]);

  // Foto do item vai para o bucket `midias` (mesmo caminho do MediaEditor).
  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'video/mp4') { setErroUpload('Envie uma imagem ou um vídeo MP4.'); return; }
    setEnviando(true); setErroUpload(null);
    try {
      const url = await onUpload(file);
      if (file.type === 'video/mp4') { setVideo(url); setImagem(null); } else { setImagem(url); setVideo(null); }
    } catch (err) {
      setErroUpload((err as Error).message ?? 'Falha no upload.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal title="Marcar item do PDV" onClose={onFechar}>
      <form onSubmit={e => { e.preventDefault(); onSalvar({ papel, nome_exibicao: nome || null, frase: frase || null, imagem_url: imagem, video_url: video }); }} className="w-full max-w-md space-y-4 rounded-2xl border border-white/[0.08] bg-[#0f0f17] p-5 shadow-2xl">
        <div><h2 className="font-semibold text-white">{item.nome}</h2><p className="text-xs text-white/40">{item.categoria ?? 'Sem categoria'} · R$ {item.preco?.toFixed(2) ?? '—'}</p></div>
        <label className="block text-xs text-white/60">Papel<select value={papel} onChange={e => setPapel(e.target.value as typeof papel)} className={inputClass}><option value="nenhum">Nenhum</option><option value="destaque">Destaque</option><option value="hype">Hype do chopp</option></select></label>
        <label className="block text-xs text-white/60">Nome exibido<input value={nome} onChange={e => setNome(e.target.value)} className={inputClass} maxLength={60} /></label>
        {papel === 'destaque' && (
          <>
            <label className="block text-xs text-white/60">Frase do destaque<textarea value={frase} onChange={e => setFrase(e.target.value)} className={inputClass} rows={2} maxLength={80} /></label>
            <div className="flex items-center gap-3">
              {video ? <video src={video} muted playsInline className="h-14 w-14 rounded-lg object-cover" /> : imagem && <img src={imagem} alt="" className="h-14 w-14 rounded-lg object-cover" />}
              <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm text-white/80 hover:bg-white/5">
                {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {video ? 'Trocar vídeo' : imagem ? 'Trocar foto' : 'Adicionar mídia'}
                <input type="file" accept="image/*,video/mp4" className="hidden" onChange={e => { void handleUpload(e); }} disabled={enviando} />
              </label>
              {(imagem || video) && <button type="button" onClick={() => { setImagem(null); setVideo(null); }} className="text-xs text-white/50 hover:text-white">Remover</button>}
            </div>
            {erroUpload && <p className="text-xs text-rose-300">{erroUpload}</p>}
          </>
        )}
        <button disabled={salvando || enviando} className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 py-3 text-sm font-semibold text-white disabled:opacity-40">Salvar marcação</button>
      </form>
    </Modal>
  );
}
